/**
 * Клиент событий сервера — единственный веб-сокет приложения.
 *
 * Модель работы (спека §8.1): состояние берётся снапшотом по REST вместе с seq,
 * дальше идут только дельты. После обрыва клиент присылает hello{lastSeq}; если
 * пропущенное укладывается в окно сервера, он досылает его, иначе отвечает desync —
 * и тогда снапшот запрашивается заново. Своей ретрансляции и своего порядка у клиента нет.
 *
 * Токен в query-строке не передаётся никогда: сессия едет httpOnly-cookie,
 * а URL попадает в access-log и Referer.
 *
 * Кадры протокола берутся из @cc/shared: описание одно на сервер и на браузер (§8.1).
 * Своих определений здесь нет — разъехавшийся протокол виден не компилятором,
 * а молчащим пультом.
 */

import type { ClientMsg, RealtimeEventType, ServerMsg } from '@cc/shared';

export type { ClientMsg, RealtimeEventType, ServerMsg };

/** Комнату выдаёт сервер, клиент только просит — поэтому здесь строка (§8.1) */
export type RoomName = string;

export type RealtimeStatus = 'offline' | 'connecting' | 'live';

/** Пауза переподключения: 0,5 → 15 с с jitter (спека §8.1) */
const MIN_RECONNECT_MS = 500;
const MAX_RECONNECT_MS = 15_000;

export interface RealtimeClientOptions {
  /** Комнаты, которые клиент просит. Фактический список решает сервер и вернёт в ready */
  rooms: RoomName[];
  onEvent: (event: { seq: number; room: RoomName; type: RealtimeEventType; payload: unknown }) => void;
  /** Соединение готово: сюда приходит фактический список комнат */
  onReady: (ready: { seq: number; rooms: RoomName[] }) => void;
  /**
   * Пропущенное не восстановить — UI обязан перезапросить снапшот целиком.
   * Показывать пользователю нечего: для него это просто обновление данных.
   */
  onDesync: (reason: 'gap' | 'too_old' | 'client_gap') => void;
  onStatus?: (status: RealtimeStatus) => void;
  onServerError?: (error: { code: string; message: string }) => void;
  /** Подменяется в тестах */
  createSocket?: (url: string) => WebSocket;
  now?: () => number;
  random?: () => number;
}

function defaultUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // Гейтвей зарегистрирован вне префикса /api (build.ts) и проксируется
  // отдельной локацией nginx — путь обязан совпадать со всеми тремя местами
  return `${protocol}//${window.location.host}/ws`;
}

export class RealtimeClient {
  readonly #options: RealtimeClientOptions;
  #socket: WebSocket | null = null;
  #rooms: RoomName[];
  #lastSeq: number | null = null;
  #attempt = 0;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #closedByUs = false;

  constructor(options: RealtimeClientOptions) {
    this.#options = options;
    this.#rooms = [...options.rooms];
  }

  /** Номер последнего доставленного события — с ним клиент возвращается после обрыва */
  get lastSeq(): number | null {
    return this.#lastSeq;
  }

  /** Снапшот пришёл по REST со своим seq — дальше поток продолжается с него */
  setBaseline(seq: number): void {
    this.#lastSeq = seq;
  }

  connect(): void {
    if (this.#socket) return;
    this.#closedByUs = false;
    this.#options.onStatus?.('connecting');

    // Адрес считается только для настоящего сокета: в тестах window нет,
    // а подменённой фабрике адрес и не нужен — она получает путь гейтвея
    const socket = this.#options.createSocket
      ? this.#options.createSocket('/ws')
      : new WebSocket(defaultUrl());
    this.#socket = socket;

    socket.addEventListener('open', () => {
      this.#attempt = 0;
      this.#send({
        t: 'hello',
        rooms: this.#rooms,
        ...(this.#lastSeq !== null ? { lastSeq: this.#lastSeq } : {}),
      });
    });
    socket.addEventListener('message', (event: MessageEvent<string>) => {
      this.#handle(event.data);
    });
    socket.addEventListener('close', () => {
      this.#socket = null;
      this.#options.onStatus?.('offline');
      if (!this.#closedByUs) this.#scheduleReconnect();
    });
    socket.addEventListener('error', () => {
      // Детали ошибки браузер не отдаёт; закрытие придёт следом и запустит переподключение
    });
  }

  close(): void {
    this.#closedByUs = true;
    if (this.#reconnectTimer !== null) {
      clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = null;
    }
    this.#socket?.close();
    this.#socket = null;
    this.#options.onStatus?.('offline');
  }

  /** Смена набора комнат на лету: переход админа между разделами */
  subscribe(rooms: RoomName[]): void {
    this.#rooms = [...rooms];
    this.#send({ t: 'subscribe', rooms });
  }

  unsubscribe(rooms: RoomName[]): void {
    this.#rooms = this.#rooms.filter((room) => !rooms.includes(room));
    this.#send({ t: 'unsubscribe', rooms });
  }

  #send(message: ClientMsg): void {
    const socket = this.#socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify(message));
  }

  #handle(raw: string): void {
    let message: ServerMsg;
    try {
      message = JSON.parse(raw) as ServerMsg;
    } catch {
      return;
    }

    switch (message.t) {
      case 'ready':
        this.#lastSeq = message.seq;
        this.#rooms = message.rooms;
        this.#options.onStatus?.('live');
        this.#options.onReady({ seq: message.seq, rooms: message.rooms });
        return;

      case 'event': {
        const last = this.#lastSeq;
        // Повтор после досылки — пропускаем молча.
        if (last !== null && message.seq <= last) return;
        // Скачок номера — НЕ потеря. seq глобальный на весь журнал, а клиенту
        // приходят только его комнаты, поэтому пропуски в нумерации — чужие
        // события и норма живого потока: сокет упорядочен и ничего не теряет.
        // Настоящая потеря случается только на переподключении, и её ловит
        // сервер по hello{lastSeq} — ответом desync, когда окно журнала уехало.
        // Прежняя проверка «seq > last + 1 → desync» превращала каждое событие
        // после чужого в перезапрос снапшота, а его payload выбрасывала — так
        // терялся call.answered, и таймер разговора не стартовал.
        this.#lastSeq = message.seq;
        // payload в схеме — z.unknown(), поэтому ключ выписывается явно:
        // подписчик всегда получает поле, даже если сервер прислал событие без тела
        this.#options.onEvent({
          seq: message.seq,
          room: message.room,
          type: message.type,
          payload: message.payload,
        });
        return;
      }

      case 'ping':
        this.#send({ t: 'pong', ts: message.ts });
        return;

      case 'desync':
        this.#options.onDesync(message.reason);
        return;

      case 'error':
        this.#options.onServerError?.({ code: message.code, message: message.message });
        return;
    }
  }

  #scheduleReconnect(): void {
    if (this.#reconnectTimer !== null) return;
    const random = this.#options.random ?? Math.random;
    this.#attempt += 1;
    const base = Math.min(MAX_RECONNECT_MS, MIN_RECONNECT_MS * 2 ** (this.#attempt - 1));
    const delay = Math.round(base * (1 + 0.3 * random()));
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null;
      this.connect();
    }, delay);
  }
}
